"use client";
import React, { useState } from "react";
import { ChevronRight, ChevronLeft, Eye, EyeOff, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getClientAuthProvider } from "@/lib/auth/config";
import { EntraSignInPanel } from "@/components/auth/EntraSignInPanel";

export const SignUpWizard = () => {
    if (getClientAuthProvider() === "entra") {
        return <EntraSignInPanel mode="signup" />;
    }
    return <SupabaseSignUpWizard />;
};

function SupabaseSignUpWizard() {
    const [step, setStep] = useState(1);
    const { signUp } = useAuth();
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        fullName: "",
        email: "",
        password: "",
        confirmPassword: "",
        companyName: "",
        industry: "",
        otherIndustry: "",
        website: "https://",
        employeeCount: "1-10 employees",
        country: "",
        reportingCurrency: "USD",
        agreedToTerms: false
    });

    const countries = [
        "United States", "United Kingdom", "United Arab Emirates", "India", "Germany", "France", "Canada", "Australia", "Japan", "China", "Brazil", "Singapore", "Saudi Arabia", "South Africa", "Israel"
    ].sort(); // More can be added or fetched

    const currencies = [
        { code: "USD", label: "US Dollar ($)" },
        { code: "AED", label: "UAE Dirham (د.إ)" },
        { code: "EUR", label: "Euro (€)" },
        { code: "GBP", label: "British Pound (£)" },
        { code: "INR", label: "Indian Rupee (₹)" },
        { code: "SGD", label: "Singapore Dollar (S$)" },
        { code: "CAD", label: "Canadian Dollar (C$)" },
        { code: "AUD", label: "Australian Dollar (A$)" },
        { code: "JPY", label: "Japanese Yen (¥)" },
        { code: "SAR", label: "Saudi Riyal (ر.س)" }
    ];

    const steps = [
        { id: 1, title: "Setup your account", subtitle: "Let's start setting up Savira." },
        { id: 2, title: "Data privacy and terms consent", subtitle: "Review and accept our policies." }
    ];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target as any;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
        }));
    };

    const nextStep = () => {
        if (step < 2) setStep(step + 1);
        else handleFinish();
    };

    const prevStep = () => {
        if (step > 1) setStep(step - 1);
    };

    const handleFinish = async () => {
        await signUp(formData);
    };

    const inputClasses = "w-full px-3 py-2.5 bg-card border border-line-2 rounded-lg text-ink font-semibold placeholder:text-ink-3 placeholder:font-semibold focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent transition-all text-sm";
    const labelClasses = "block text-[13px] font-bold text-ink-2 mb-1.5";

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                {step > 1 && (
                    <button onClick={prevStep} className="flex items-center gap-1 text-sm text-ink-2 hover:text-ink mb-4 transition-colors">
                        <ChevronLeft className="w-4 h-4" />
                        Back
                    </button>
                )}
                <h2 className="text-[clamp(21px,3vw,28px)] leading-tight font-extrabold tracking-[-0.025em] text-ink mb-2">{steps[step - 1].title}</h2>
                <p className="text-ink-3 text-sm font-semibold">{steps[step - 1].subtitle}</p>
            </div>

            {/* Step 1: Account Setup */}
            {step === 1 && (
                <div className="space-y-5">
                    <div>
                        <label className={labelClasses}>Full name</label>
                        <input name="fullName" value={formData.fullName} onChange={handleChange} placeholder="e.g. James Jacobe" className={inputClasses} />
                    </div>
                    <div>
                        <label className={labelClasses}>Email</label>
                        <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="e.g. jamesjacobe@gmail.com" className={inputClasses} />
                    </div>
                    <div className="relative">
                        <label className={labelClasses}>Password</label>
                        <input
                            name="password"
                            type={showPassword ? "text" : "password"}
                            value={formData.password}
                            onChange={handleChange}
                            className={inputClasses}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-4 top-[42px] text-ink-3 hover:text-ink-2"
                        >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                    <div className="relative">
                        <label className={labelClasses}>Confirm password</label>
                        <input
                            name="confirmPassword"
                            type={showPassword ? "text" : "password"}
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            className={inputClasses}
                        />
                    </div>
                </div>
            )}

            {/* Step 2: Consent */}
            {step === 2 && (
                <div className="space-y-6">
                    <div className="bg-sunk p-4 rounded-lg max-h-[240px] overflow-y-auto text-sm text-ink-2 leading-relaxed">
                        <p className="mb-4">By using this website, you acknowledge and agree to our Terms of Service and Privacy Policy. We are committed to protecting your personal information and ensuring transparency in how we collect, use, and store your data.</p>
                        <p className="mb-4">When you interact with our site, we may collect personal details such as your name, email address, and usage data through forms, cookies, and tracking technologies to enhance your experience, improve our services, and deliver personalized content.</p>
                        <p>We process this data based on your consent, our legitimate business interests, contractual obligations, or legal compliance. Your data may be shared with trusted third-party providers under strict confidentiality agreements but will never be sold.</p>
                    </div>
                    <div className="flex items-start gap-3">
                        <div className="relative flex items-center h-5">
                            <input
                                id="agreedToTerms"
                                name="agreedToTerms"
                                type="checkbox"
                                checked={formData.agreedToTerms}
                                onChange={handleChange}
                                className="w-5 h-5 rounded-md border-line-2 accent-accent text-accent focus:ring-accent cursor-pointer"
                            />
                        </div>
                        <label htmlFor="agreedToTerms" className="text-sm text-ink-2 cursor-pointer">
                            I agree to the <span className="text-accent font-bold underline-offset-4 hover:underline cursor-pointer">Terms of service</span> and <span className="text-accent font-bold underline-offset-4 hover:underline cursor-pointer">Privacy policy.</span>
                        </label>
                    </div>
                </div>
            )}

            {/* Footer Action */}
            <button
                onClick={nextStep}
                disabled={step === 1 && (!formData.fullName || !formData.email || !formData.password)}
                className="w-full border border-accent bg-accent hover:bg-accent-hover disabled:bg-line-2 disabled:border-line-2 text-accent-ink py-3 rounded-lg text-[15px] font-bold flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
            >
                <span>{step === 2 ? "Complete registration" : "Continue"}</span>
                <ChevronRight className="w-5 h-5" />
            </button>

            {/* Switch to Login */}
            <p className="text-center text-sm text-ink-3">
                Already have an account? <span onClick={() => { }} className="text-accent font-bold underline-offset-4 hover:underline cursor-pointer">Sign in</span>
            </p>
        </div>
    );
};
